/**
 * Locale-aware number formatting.
 *
 * `formatNumber` / `formatPercent` are for counts and ratios that genuinely
 * live in a JS number. Quantities arrive on the wire as decimal strings
 * (Postgres NUMERIC(20,6)) and MUST go through `formatQuantity`, which formats
 * via BigInt + formatToParts so no digit ever passes through a lossy
 * `Number()` conversion. Money has its own lib — this module never handles
 * `{ amount, currency }` values.
 */

interface NumberSymbols {
  /** Grouped integer formatter — fed BigInt only, never a JS number. */
  integerFormat: Intl.NumberFormat;
  /** Locale digit for each ASCII digit 0–9 (index = digit value). */
  digits: readonly string[];
  /** Locale decimal separator (e.g. "." or "٫"). */
  decimal: string;
  /** Everything the locale prefixes a negative number with (e.g. "-" or "؜-"). */
  negativePrefix: string;
}

const symbolsCache = new Map<string, NumberSymbols>();

function getSymbols(locale: string): NumberSymbols {
  const cached = symbolsCache.get(locale);
  if (cached) return cached;

  const plain = new Intl.NumberFormat(locale, { useGrouping: false });
  const digits = Array.from({ length: 10 }, (_, d) => plain.format(d));
  const decimal =
    new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
      .formatToParts(0.1)
      .find((part) => part.type === "decimal")?.value ?? ".";

  let negativePrefix = "";
  for (const part of plain.formatToParts(-1)) {
    if (part.type === "integer") break;
    negativePrefix += part.value;
  }
  if (!negativePrefix) negativePrefix = "-"; // suffix-negative locales: fall back to a plain sign

  const symbols: NumberSymbols = {
    integerFormat: new Intl.NumberFormat(locale, { useGrouping: true, maximumFractionDigits: 0 }),
    digits,
    decimal,
    negativePrefix,
  };
  symbolsCache.set(locale, symbols);
  return symbols;
}

function mapDigits(asciiDigits: string, symbols: NumberSymbols): string {
  let out = "";
  for (const ch of asciiDigits) {
    out += symbols.digits[ch.charCodeAt(0) - 48] ?? ch;
  }
  return out;
}

/**
 * Format a plain JS number (counts, row totals, ratios — NEVER money or
 * wire quantities; those are decimal strings and must not touch a JS number).
 */
export function formatNumber(
  value: number,
  locale: string,
  opts?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(locale, opts).format(value);
}

/**
 * Format a fraction as a locale percentage: `formatPercent(0.156, "en") === "15.6%"`.
 * Defaults to one fraction digit; override via `opts.maximumFractionDigits`.
 */
export function formatPercent(
  fraction: number,
  locale: string,
  opts?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(locale, {
    style: "percent",
    maximumFractionDigits: 1,
    ...opts,
  }).format(fraction);
}

const DECIMAL_STRING_RE = /^([+-]?)(\d+)(?:\.(\d+))?$/;

/**
 * Format a wire decimal string (NUMERIC(20,6) quantity) for display without
 * precision loss: the integer part goes through the locale formatter as a
 * BigInt (grouping + locale digits), the fraction digits are mapped to locale
 * digits one by one. `Number()` on the input is forbidden and never happens.
 *
 * Fractions longer than `maxFractionDigits` (default 6, the wire scale) are
 * rounded half-away-from-zero in BigInt arithmetic; trailing fraction zeros
 * are trimmed ("12.000000" → "12"). Throws RangeError on non-canonical input
 * ("", "1.2.3", "1e5", ".5", non-ASCII digits).
 */
export function formatQuantity(
  decimalString: string,
  locale: string,
  maxFractionDigits = 6,
): string {
  if (!Number.isInteger(maxFractionDigits) || maxFractionDigits < 0) {
    throw new RangeError(
      `maxFractionDigits must be a non-negative integer, got ${maxFractionDigits}`,
    );
  }
  const m = DECIMAL_STRING_RE.exec(decimalString);
  if (!m) {
    throw new RangeError(`Invalid decimal string: "${decimalString}"`);
  }
  const negative = m[1] === "-";
  let integerDigits = m[2] ?? "0";
  let fractionDigits = m[3] ?? "";

  if (fractionDigits.length > maxFractionDigits) {
    // Round half-away-from-zero on the scaled magnitude, in BigInt.
    const kept = fractionDigits.slice(0, maxFractionDigits);
    const nextDigit = fractionDigits.charCodeAt(maxFractionDigits) - 48;
    let scaled = BigInt(integerDigits + kept);
    if (nextDigit >= 5) scaled += 1n;
    const s = scaled.toString().padStart(maxFractionDigits + 1, "0");
    integerDigits = maxFractionDigits === 0 ? s : s.slice(0, -maxFractionDigits);
    fractionDigits = maxFractionDigits === 0 ? "" : s.slice(-maxFractionDigits);
  }
  fractionDigits = fractionDigits.replace(/0+$/, "");

  const symbols = getSymbols(locale);
  const grouped = symbols.integerFormat.format(BigInt(integerDigits));
  const isZero = !/[1-9]/.test(integerDigits + fractionDigits);
  const sign = negative && !isZero ? symbols.negativePrefix : "";
  const fraction = fractionDigits ? symbols.decimal + mapDigits(fractionDigits, symbols) : "";
  return sign + grouped + fraction;
}
